import axios from "axios";
import { logClient } from "./logger";
import { isProbablyJwt } from "./utils/jwt";

const baseURL =
  typeof window !== "undefined" && window.__PRERENDER_API_BASE_URL
    ? window.__PRERENDER_API_BASE_URL
    : "/api";

function normalizeErrorData(data) {
  try {
    if (typeof data === "string") {
      const hasHtml = data.toLowerCase().includes("<html");
      if (hasHtml) {
        const titleMatch = data.match(/<title[^>]*>([^<]*)<\/title>/i);
        const title = titleMatch && titleMatch[1] ? titleMatch[1].trim() : null;
        // strip scripts/styles then tags; keep some whitespace
        const withoutScript = data
          .replace(/<script[\s\S]*?<\/script>/gi, " ")
          .replace(/<style[\s\S]*?<\/style>/gi, " ");
        const bodyText = withoutScript
          .replace(/<br\s*\/?>/gi, "\n")
          .replace(/<[^>]+>/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        return title ? `${title}: ${bodyText}` : bodyText || data;
      }
    }
  } catch (e) {
    /* ignore */
  }
  return data;
}

const isProtectedPath = (url = "") => {
  try {
    // Normalize to path without query
    const u = String(url);
    return u.startsWith("/me/") || u === "/me" || u === "/users/me";
  } catch (e) {
    return false;
  }
};

const isFacebookIntegrationPath = (url = "") =>
  String(url).startsWith("/admin/facebook/");

const isPublicApiPath = (url = "") => {
  const path = String(url).split("?", 1)[0];
  return (
    path.startsWith("/public/") ||
    path.startsWith("/auth/") ||
    path === "/admin/login" ||
    path === "/staff/login"
  );
};

const clearStoredAuthTokens = () => {
  try {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
  } catch (e) {}
};

let lastAuthExpiredEventAt = 0;

const handleExpiredSession = (config) => {
  const url = config?.url || "";
  if (isPublicApiPath(url) || isFacebookIntegrationPath(url)) return false;

  let storedToken = null;
  try {
    storedToken =
      localStorage.getItem("token") || sessionStorage.getItem("token");
  } catch (e) {}
  if (
    !config?.__sessionAuth ||
    !storedToken ||
    (config.__sessionToken && config.__sessionToken !== storedToken)
  ) {
    return false;
  }

  clearStoredAuthTokens();
  const now = Date.now();
  if (now - lastAuthExpiredEventAt < 2000) return true;
  lastAuthExpiredEventAt = now;
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("auth:expired"));
    }
  } catch (e) {}
  return true;
};

const instance = axios.create({ baseURL });
instance.interceptors.request.use((config) => {
  let token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (
    token &&
    (token === "null" || token === "undefined" || !isProbablyJwt(token))
  ) {
    try {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
    } catch (e) {}
    token = null;
  }
  // Respect explicit Authorization header passed by callers (for public intercom token)
  try {
    const existing = config && config.headers && config.headers.Authorization;
    // If no token and the request targets protected /me endpoints, cancel early to avoid noisy 401s when logged out
    if (!token && !existing && isProtectedPath(config?.url || "")) {
      return Promise.reject(new axios.Cancel("auth: missing token"));
    }
    if (!existing && token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      config.__sessionAuth = true;
      config.__sessionToken = token;
    }
  } catch (e) {
    /* ignore */
  }
  return config;
});
// Response interceptor: redirect to themed error pages for common HTTP errors
instance.interceptors.response.use(
  (r) => {
    try {
      if (typeof window !== "undefined")
        window.dispatchEvent(new CustomEvent("maintenance:hide"));
    } catch (e) {}
    return r;
  },
  (err) => {
    try {
      const cfg = err?.config || {};
      const status = err.response?.status;
      if (status === 401 && handleExpiredSession(cfg)) {
        return Promise.reject(err);
      }
      if (cfg.skipHttpErrorEvent) {
        return Promise.reject(err);
      }
      // allow callers to opt-out of global error redirect by setting config.skipErrorRedirect = true
      if (cfg.skipErrorRedirect) {
        return Promise.reject(err);
      }
      // allow callers (like the maintenance page) to opt-out of automatic maintenance redirect
      if (cfg.skipMaintenanceRedirect) {
        return Promise.reject(err);
      }
      if (status) {
        if (status === 401 && isFacebookIntegrationPath(cfg.url)) {
          return Promise.reject(err);
        }
        const redirectCodes = [
          400, 401, 402, 403, 404, 405, 408, 418, 429, 500, 502, 503, 504,
        ];
        if (redirectCodes.includes(status)) {
          // Emit a global HTTP error event instead of redirecting so UI can show a modal
          try {
            const payload = {
              status,
              statusText: err.response?.statusText,
              data: normalizeErrorData(err.response?.data),
              config: cfg,
            };
            if (typeof window !== "undefined")
              window.dispatchEvent(
                new CustomEvent("http:error", { detail: payload }),
              );
          } catch (e) {}
          return Promise.reject(err);
        }
        if (status >= 500) {
          try {
            const payload = {
              status,
              statusText: err.response?.statusText,
              data: normalizeErrorData(err.response?.data),
              config: cfg,
            };
            if (typeof window !== "undefined")
              window.dispatchEvent(
                new CustomEvent("http:error", { detail: payload }),
              );
          } catch (e) {}
          return Promise.reject(err);
        }
      }
      // No response at all (network error / backend down) -> show maintenance page
      if (!err.response) {
        try {
          // Don't redirect if caller opted out
          if (cfg.skipMaintenanceRedirect) return Promise.reject(err);
          // If already on maintenance page, don't force reload (prevents loop)
          if (
            typeof window !== "undefined" &&
            window.location &&
            window.location.pathname &&
            window.location.pathname.startsWith("/maintenance")
          ) {
            return Promise.reject(err);
          }
          // Throttle redirects per-tab (avoid repeated full-page navigations)
          if (typeof window !== "undefined" && window.sessionStorage) {
            const last = window.sessionStorage.getItem("maintenanceRedirectAt");
            const now = Date.now();
            if (last && now - Number(last) < 5000) {
              return Promise.reject(err);
            }
            window.sessionStorage.setItem("maintenanceRedirectAt", String(now));
          }
          // preserve intended destination so maintenance page can return user after recovery
          if (typeof window !== "undefined") {
            const from = encodeURIComponent(
              window.location.pathname + (window.location.search || ""),
            );
            // dispatch global event to show overlay
            try {
              window.dispatchEvent(
                new CustomEvent("maintenance:show", { detail: { from } }),
              );
            } catch (e) {}
            // Previously returned a never-resolving Promise here which can hang tests and importers.
            // Return a rejected promise instead so callers/tests get an immediate rejection and proceed.
            return Promise.reject(err);
          }
        } catch (e) {}
        return Promise.reject(err);
      }
    } catch (e) {}
    return Promise.reject(err);
  },
);
export default instance;
